import React from "react";
import { AxiosError } from "axios";
import { Alert as JoyUiAlert, Box } from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";
import { CheckCircle, AlertCircle, AlertTriangle } from "lucide-react";
import Button from "../Button";

interface IApiErrorResponse {
    StatusCode?: number;
    Success?: boolean;
    Error?:
        | {
              Name?: string;
              Message?: string;
              Stack?: string;
          }
        | string;
    message?: string;
    MetaData?: {
        RequestId: string;
        InitTimestamp: string;
        CompleteTimestamp: string;
    };
}

interface IParsedError {
    displayMessage: string;
    rawData?: any;
}

interface IAlertProps {
    successMessage?: string | React.ReactElement | null | false;
    errorMessage?: string | boolean | Error | AxiosError | IApiErrorResponse | unknown | null | false;
    warningMessage?: string | React.ReactElement | null | false;
    sx?: SxProps;
    [key: string]: any;
}

/**
 * Parses API error responses and extracts user-friendly error messages
 */
export function parseError(
    error: string | boolean | Error | AxiosError | IApiErrorResponse | unknown | null | undefined,
): IParsedError | null {
    if (!error) {
        return null;
    }

    // Default response structure
    const result: IParsedError = {
        displayMessage: "An error occurred. Please try again.",
        rawData: error,
    };

    // Handle boolean flags
    if (typeof error === "boolean") {
        if (!error) return null;
        result.displayMessage = "An error occurred. Please try again.";
        return result;
    }

    // Handle string errors
    if (typeof error === "string") {
        result.displayMessage = error;
        return result;
    }

    // Handle Error objects
    if (error instanceof Error) {
        result.displayMessage = error.message;
        return result;
    }

    // Handle AxiosError
    if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as AxiosError<IApiErrorResponse>;
        if (axiosError.response) {
            const data = axiosError.response.data;
            result.rawData = data;

            if (data?.Error) {
                if (typeof data.Error === "string") {
                    result.displayMessage = data.Error;
                } else if (typeof data.Error === "object" && data.Error.Message) {
                    result.displayMessage = data.Error.Message;
                }
            } else if (data?.message) {
                result.displayMessage = data.message;
            } else {
                const status = axiosError.response.status;
                if (status === 401) result.displayMessage = "Unauthorized. Please log in again.";
                else if (status === 403) result.displayMessage = "You don't have permission to perform this action.";
                else if (status === 404) result.displayMessage = "The requested resource was not found.";
                else if (status === 500) result.displayMessage = "Server error. Please try again later.";
            }
            return result;
        }
    }

    // Fallback for objects with message
    if (error && typeof error === "object" && "message" in error) {
        result.displayMessage = (error as any).message;
    }

    return result;
}

export default function Alert(props: IAlertProps) {
    const { successMessage, errorMessage, warningMessage, sx, ...otherProps } = props;

    // Default border styles that are automatically applied
    const defaultSx: SxProps = {
        border: "1px solid",
        borderColor: "divider",
    };

    // Merge default styles with custom sx prop
    // If sx is provided, merge it with defaultSx (custom sx will override defaults)
    const mergedSx: SxProps = sx
        ? [
              defaultSx,
              ...(Array.isArray(sx) ? sx : [sx]), // Handle both array and object sx
          ]
        : defaultSx;

    // Parse error message
    const errorResult = parseError(errorMessage);

    const handleReport = () => {
        const raw = errorResult?.rawData || {};
        const reportData = {
            ErrorName: raw.Error?.Name || "UnknownError",
            ErrorMessage: raw.Error?.Message || errorResult?.displayMessage,
            RequestId: raw.MetaData?.RequestId,
            InitTimestamp: raw.MetaData?.InitTimestamp,
            CompleteTimestamp: raw.MetaData?.CompleteTimestamp,
            StackTrace: raw.Error?.Stack,
        };

        console.log("---------- ERROR REPORT ----------");
        console.table(reportData);
        console.log("Full Raw Data:", raw);
        console.log("----------------------------------");
        alert("Thank you! The detailed error report has been logged to the console for processing.");
    };

    if (errorResult) {
        return (
            <JoyUiAlert
                variant="soft"
                startDecorator={<AlertCircle size={20} />}
                color="danger"
                sx={{
                    ...mergedSx,
                    alignItems: "flex-start",
                    gap: 1.5,
                }}
                {...otherProps}
            >
                <Box sx={{ flex: 1 }}>
                    {errorResult.displayMessage}
                    <Box mt={1}>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="danger"
                            onClick={handleReport}
                            sx={{
                                borderRadius: 8,
                                backgroundColor: "rgba(255, 255, 255, 0.5)",
                                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.8)" },
                                alignSelf: { xs: "stretch", sm: "center" },
                            }}
                        >
                            Report
                        </Button>
                    </Box>
                </Box>
            </JoyUiAlert>
        );
    }

    // Handle warning message - can be string, React element, or falsy
    if (warningMessage) {
        // Check if it's a React element
        if (React.isValidElement(warningMessage)) {
            return (
                <JoyUiAlert
                    variant="soft"
                    startDecorator={<AlertTriangle size={24} />}
                    color="warning"
                    sx={mergedSx}
                    {...otherProps}
                >
                    {warningMessage}
                </JoyUiAlert>
            );
        }

        // Handle string warning message
        if (typeof warningMessage === "string") {
            return (
                <JoyUiAlert
                    variant="soft"
                    startDecorator={<AlertTriangle size={24} />}
                    color="warning"
                    sx={mergedSx}
                    {...otherProps}
                >
                    {warningMessage}
                </JoyUiAlert>
            );
        }
    }

    // Handle success message - can be string, React element, or falsy
    if (successMessage) {
        // Check if it's a React element
        if (React.isValidElement(successMessage)) {
            return (
                <JoyUiAlert
                    startDecorator={<CheckCircle size={20} />}
                    variant="soft"
                    color="success"
                    sx={mergedSx}
                    {...otherProps}
                >
                    {successMessage}
                </JoyUiAlert>
            );
        }

        // Handle string success message
        if (typeof successMessage === "string") {
            return (
                <JoyUiAlert
                    startDecorator={<CheckCircle size={20} />}
                    variant="soft"
                    color="success"
                    sx={mergedSx}
                    {...otherProps}
                >
                    {successMessage}
                </JoyUiAlert>
            );
        }
    }

    return null;
}
